import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import PairingSection from "../components/v2_demo/PairingSection";

const BASE = "/whatsapp/v2/demo-runtime";
const POLL_MS = 4000;

const _s = {
  wrap:    { padding: 32, maxWidth: 660, margin: "0 auto", fontFamily: "monospace", fontSize: 14 },
  banner:  { background: "#fffbeb", border: "1px solid #f59e0b", borderRadius: 8, padding: "10px 16px", marginBottom: 20, fontSize: 13 },
  err:     { background: "#fef2f2", border: "1px solid #ef4444", borderRadius: 8, padding: "10px 16px", marginBottom: 16, color: "#dc2626" },
  info:    { background: "#eff6ff", border: "1px solid #93c5fd", borderRadius: 8, padding: "10px 16px", marginBottom: 16, color: "#1d4ed8", fontSize: 13 },
  warn:    { background: "#fff7ed", border: "1px solid #fb923c", borderRadius: 8, padding: "10px 16px", marginBottom: 16, color: "#c2410c", fontSize: 13 },
  btns:    { display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" },
  btn:     (active, color) => ({ padding: "8px 18px", background: active ? color : "#d1d5db", color: "#fff", border: "none", borderRadius: 6, cursor: active ? "pointer" : "not-allowed", fontWeight: 700, fontSize: 13 }),
  qrBox:   { minHeight: 230, border: "1px solid #e5e7eb", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "#f9fafb", marginBottom: 20 },
  qrHint:  { color: "#9ca3af", fontSize: 13, textAlign: "center", lineHeight: 1.6 },
  table:   { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  tdLabel: { padding: "5px 8px", color: "#6b7280", width: 180, verticalAlign: "top" },
  tdVal:   { padding: "5px 8px", fontWeight: 500, wordBreak: "break-all" },
  tr:      { borderBottom: "1px solid #f3f4f6" },
  badge:   (ok) => ({ display: "inline-block", padding: "1px 8px", borderRadius: 99, fontSize: 12, fontWeight: 700, background: ok ? "#dcfce7" : "#f3f4f6", color: ok ? "#166534" : "#374151" }),
  title:   { fontSize: 18, fontWeight: 700, marginBottom: 6 },
  sub:     { color: "#6b7280", marginBottom: 20, fontSize: 13 },
};

export default function V2DemoRuntimePage() {
  const { authAxios, user } = useAuth();
  const expectedTenantId = user?.restaurant_id || null;
  const [status, setStatus] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [qrExpiresAt, setQrExpiresAt] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resetMsg, setResetMsg] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await authAxios.get(`${BASE}/status`);
      setStatus(r.data);
      setError(null);
    } catch (e) {
      const code = e.response?.status;
      if (code === 403) setError("Tenant não autorizado — configura WA_V2_DEMO_RUNTIME_ALLOWED_TENANT.");
      else if (code === 503) setError("V2 Demo Runtime desativado — activa WA_V2_DEMO_RUNTIME_ENABLED=true no Render.");
    }
  }, [authAxios]);

  const fetchQr = useCallback(async () => {
    try {
      const r = await authAxios.get(`${BASE}/qr`);
      setQrDataUrl(r.data?.qr_data_url || null);
      setQrExpiresAt(r.data?.expires_at || null);
    } catch {
      setQrDataUrl(null);
      setQrExpiresAt(null);
    }
  }, [authAxios]);

  const statusTenantId = status?.tenant_id || null;
  const tenantMismatch = Boolean(
    expectedTenantId && statusTenantId && statusTenantId !== expectedTenantId,
  );

  useEffect(() => {
    if (!expectedTenantId) return undefined;
    fetchStatus();
    fetchQr();
    const poll = setInterval(() => {
      if (tenantMismatch) return;
      fetchStatus();
      fetchQr();
    }, POLL_MS);
    return () => clearInterval(poll);
  }, [fetchStatus, fetchQr, expectedTenantId, tenantMismatch]);

  const handleConnect = async () => {
    setBusy(true); setResetMsg(null);
    try {
      await authAxios.post(`${BASE}/connect`);
      await fetchStatus(); await fetchQr();
    } catch (e) {
      const code = e.response?.status;
      if (code === 403) setError("Tenant não autorizado");
      else if (code === 503) setError("V2 Demo Runtime desativado");
    } finally { setBusy(false); }
  };

  const handleDisconnect = async () => {
    setBusy(true); setResetMsg(null);
    try {
      await authAxios.post(`${BASE}/disconnect`);
      setQrDataUrl(null); setQrExpiresAt(null);
      await fetchStatus();
    } catch { /* ignore */ } finally { setBusy(false); }
  };

  // Reset: para o lifecycle + apaga sessão V2 demo → força novo QR na próxima ligação.
  // Nunca toca V1 sessions.
  const handleReset = async () => {
    setBusy(true); setResetMsg(null);
    try {
      await authAxios.post(`${BASE}/reset`);
      setQrDataUrl(null); setQrExpiresAt(null);
      setResetMsg("Sessão V2 limpa — clique em CONNECT para gerar novo QR.");
      await fetchStatus();
    } catch (e) {
      const code = e.response?.status;
      if (code === 403) setError("Tenant não autorizado");
      else setResetMsg("Erro ao resetar — tenta de novo.");
    } finally { setBusy(false); }
  };

  const running = status?.running || false;
  const qrExpired = status?.qr_expired || false;
  const st = status?.state || "—";
  const lt = status?.last_transition;
  const stuckConnecting = running && st === "CONNECTING";

  let qrHint = "Clique em CONNECT para gerar QR";
  if (running && st === "WAITING_QR") qrHint = "Aguardar QR do WhatsApp…";
  else if (stuckConnecting) qrHint = "A conectar ao WhatsApp… (max 90s — podes clicar DISCONNECT se preso)";
  else if (running && st === "CONNECTED") qrHint = "Ligado — sem QR necessário";
  else if (!running && qrExpired) qrHint = "QR expirado — clique em CONNECT para gerar novo";
  else if (!running && status !== null) qrHint = "Clique em CONNECT para ligar";

  const expiresLabel = qrExpiresAt ? new Date(qrExpiresAt).toLocaleTimeString() : null;

  const rows = status ? [
    ["Tenant (JWT)",      expectedTenantId || "—"],
    ["Tenant (runtime)", statusTenantId || "—"],
    ["Estado",            st],
    ["Running",           <span style={_s.badge(running)}>{running ? "YES" : "NO"}</span>],
    ["QR disponível",     <span style={_s.badge(status.qr_available)}>{String(status.qr_available)}</span>],
    ["QR expirado",       String(status.qr_expired ?? "—")],
    ["QR expira em",      expiresLabel || "—"],
    ["Reconexões",        String(status.reconnect_count ?? 0)],
    ["Heartbeat activo",  <span style={_s.badge(status.heartbeat_active)}>{String(status.heartbeat_active)}</span>],
    ["Instance ID",       status.instance_id || "—"],
    ["Última transição",  lt ? `${lt.from} → ${lt.to}` : "—"],
    ["Razão",             lt?.reason || "—"],
    ["Histórico",         `${status.history_count ?? 0} transições`],
    ["Actualizado",       status.updated_at ? new Date(status.updated_at).toLocaleTimeString() : "—"],
  ] : [];

  return (
    <div style={_s.wrap}>
      <div style={_s.title}>V2 Demo Runtime</div>
      <div style={_s.sub}>Página de teste temporária · owner-only · polling a cada {POLL_MS / 1000}s</div>

      <div style={_s.banner}>
        ⚠️ Esta página usa o <strong>V2 Demo Runtime</strong>. Não usa /chatbot nem V1. Apenas tenant demo autorizado.
      </div>

      {tenantMismatch && (
        <div style={_s.err}>
          Polling pausado: sessão JWT ({expectedTenantId?.slice(0, 8)}…) ≠ runtime ({statusTenantId?.slice(0, 8)}…).
          Feche abas antigas do demo ou use /chatbot para o tenant de produção.
        </div>
      )}
      {error && <div style={_s.err}>🚫 {error}</div>}
      {resetMsg && !error && !tenantMismatch && <div style={_s.info}>{resetMsg}</div>}
      {!running && qrExpired && !error && !resetMsg && (
        <div style={_s.info}>QR expirou — clique em CONNECT para gerar novo QR ou RESETAR se persistir.</div>
      )}
      {stuckConnecting && (
        <div style={_s.warn}>A ligar… Se demorar mais de 90s clica DISCONNECT e tenta de novo, ou RESETAR SESSÃO.</div>
      )}

      <div style={_s.btns}>
        <button onClick={handleConnect} disabled={busy || running} style={_s.btn(!busy && !running, "#16a34a")}>
          {busy ? "…" : "CONNECT"}
        </button>
        <button onClick={handleDisconnect} disabled={busy || !running} style={_s.btn(!busy && running, "#dc2626")}>
          {busy ? "…" : "DISCONNECT"}
        </button>
        <button onClick={handleReset} disabled={busy} style={_s.btn(!busy, "#6b7280")}>
          {busy ? "…" : "RESETAR SESSÃO"}
        </button>
      </div>

      <div style={_s.qrBox}>
        {qrDataUrl
          ? <img src={qrDataUrl} alt="QR V2" style={{ width: 200, height: 200 }} />
          : <span style={_s.qrHint}>{qrHint}</span>}
      </div>

      {status && (
        <table style={_s.table}>
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k} style={_s.tr}>
                <td style={_s.tdLabel}>{k}</td>
                <td style={_s.tdVal}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!error && (
        <PairingSection
          authAxios={authAxios}
          busy={busy}
          setBusy={setBusy}
          running={running}
        />
      )}
    </div>
  );
}
